var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Spinner, Tooltip } from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDeepCompareEffect, useFirstMountState } from 'react-use';
import { conversationsApi, } from 'apis/conversations';
import { getConversationMessageDeliveryStatusAttributes } from 'utils/conversation';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { ConversationMessageDeliveryReport } from '../conversationMessageDeliveryReport';
import styles from './conversationMessageDeliveryStatus.module.scss';
/**
 * Represents the component for displaying a computed conversation message
 * delivery status with an extended delivery status in a tooltip.
 */
export const ConversationMessageDeliveryStatus = (props) => {
    const dispatch = useAppDispatch();
    const isFirstMount = useFirstMountState();
    const { computedDeliveryEvent, csid, messageId } = props;
    const isTooltipOpen = useRef(false);
    const shouldRefetch = useRef(true);
    const [deliveryEvents, setDeliveryEvents] = useState([]);
    const [readDeliveryEventsAsyncStatus, setReadDeliveryEventsAsyncStatus] = useState('uninitialized');
    const fetchDeliveryEvents = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setReadDeliveryEventsAsyncStatus('pending');
        const response = yield dispatch(conversationsApi.endpoints.readConversationMessageDeliveryEvents.initiate({ csid, messageId, filter: 'latest_event_type' }, { forceRefetch: true }));
        if (response.data) {
            setDeliveryEvents(response.data.data.delivery_events);
            setReadDeliveryEventsAsyncStatus('fulfilled');
        }
        else {
            setReadDeliveryEventsAsyncStatus('rejected');
        }
        shouldRefetch.current = false;
    }), [csid, dispatch, messageId]);
    const handleTooltipOpenChange = useCallback((isOpen) => __awaiter(void 0, void 0, void 0, function* () {
        isTooltipOpen.current = isOpen;
        if (isOpen &&
            shouldRefetch.current &&
            readDeliveryEventsAsyncStatus !== 'pending') {
            yield fetchDeliveryEvents();
        }
    }), [fetchDeliveryEvents, readDeliveryEventsAsyncStatus]);
    const { icon } = getConversationMessageDeliveryStatusAttributes(computedDeliveryEvent.status);
    const tooltipContent = useMemo(() => {
        if (readDeliveryEventsAsyncStatus === 'uninitialized' ||
            readDeliveryEventsAsyncStatus === 'pending') {
            return _jsx(Spinner, { size: 16 });
        }
        if (readDeliveryEventsAsyncStatus === 'rejected') {
            return 'Failed getting delivery status. Try again.';
        }
        if (!deliveryEvents.length) {
            return computedDeliveryEvent.status === 'sent'
                ? 'Sent'
                : 'No delivery status available. Try again later.';
        }
        return (_jsx(ConversationMessageDeliveryReport, { computedStatus: computedDeliveryEvent.status, deliveryEvents: deliveryEvents }));
    }, [
        readDeliveryEventsAsyncStatus,
        deliveryEvents,
        computedDeliveryEvent.status,
    ]);
    useDeepCompareEffect(() => {
        if (isFirstMount) {
            return;
        }
        shouldRefetch.current = true;
        if (isTooltipOpen.current) {
            fetchDeliveryEvents();
        }
    }, [computedDeliveryEvent]);
    return (_jsx(Tooltip, Object.assign({ dark: true, wrap: "span", wrapProps: {
            className: styles.root,
        }, content: tooltipContent, onOpenChange: handleTooltipOpenChange }, { children: icon })));
};
